import { useRef, useState } from "react";
import type { WorkOrderId } from "../../lib/workIds";
import { useSaveProcedureItemMutation } from "../../mutations/useSaveProcedureItemMutation";
import {
  ProcedureItemConflictError,
  type ProcedureItemCurrent,
  type ProcedureResponseValue,
  procedureItemRevision,
  useCachedProcedureExecution,
} from "../../queries/useProcedureExecution";
import type {
  ProcedureAnswer,
  ProcedureExecutionItem,
} from "./ProcedureExecutionLayout";

/** What the page shows for one item's autosave. */
export type ItemSaveStatus =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "invalid"; message: string }
  | { kind: "error"; message: string }
  | { kind: "conflict"; current: ProcedureItemCurrent; message?: string };

/** A value to save; null clears the answer. */
type ItemValue = ProcedureResponseValue | null;

export type ProcedureItemAutosave = {
  statusFor: (itemId: string) => ItemSaveStatus;
  /**
   * Saves the answer if it differs from what is stored. Resolves once it (or a
   * newer answer queued behind it) is stored; rejects when the save fails or
   * the item is waiting on a conflict choice, so the caller keeps the draft.
   */
  commit: (
    item: ProcedureExecutionItem,
    answer: ProcedureAnswer,
  ) => Promise<void>;
  /** Resolves a conflict by sending the user's answer over the other user's. */
  keepMine: (
    item: ProcedureExecutionItem,
    answer: ProcedureAnswer,
  ) => Promise<void>;
  /** Resolves a conflict by dropping the user's answer for the stored one. */
  acceptTheirs: (itemId: string) => void;
  /**
   * Waits for every save in flight, then rejects if any item is still unsaved
   * (failed, invalid or waiting on a conflict choice).
   */
  flushAll: () => Promise<void>;
  /** An item is saving, failed or in conflict. */
  hasPending: boolean;
};

/**
 * The response to send for an answer, or undefined when it cannot be sent (an
 * invalid number). A blank text, number or choice answer clears the response.
 */
export function answerToValue(
  item: ProcedureExecutionItem,
  answer: ProcedureAnswer,
): ItemValue | undefined {
  if (item.type === "checkbox")
    return { type: "CHECKBOX", value: answer === true };
  if (item.type === "single-choice")
    return typeof answer === "string" && answer !== ""
      ? { type: "SINGLE_CHOICE", value: answer }
      : null;
  if (item.type === "numeric") {
    if (answer === undefined || answer === "") return null;
    const number = typeof answer === "number" ? answer : Number(answer);
    return Number.isFinite(number)
      ? { type: "NUMERIC", value: number }
      : undefined;
  }
  if (item.type === "text") {
    const text = typeof answer === "string" ? answer.trim() : "";
    return text ? { type: "TEXT", value: text } : null;
  }
  return undefined;
}

function normalizedValue(value: ItemValue | undefined): ItemValue {
  if (!value) return null;
  if (value.type === "PHOTO" && value.fileIds.length === 0) return null;
  return value;
}

function valuesEqual(
  left: ItemValue | undefined,
  right: ItemValue | undefined,
) {
  return (
    JSON.stringify(normalizedValue(left)) ===
    JSON.stringify(normalizedValue(right))
  );
}

const unsavedMessage =
  "Some answers are not saved yet. Resolve them to continue.";

/**
 * Saves each Procedure answer as the user works, one item at a time. An item
 * has at most one request in flight; a newer answer waits behind it and is sent
 * with the token the first save returned, so rapid edits never conflict with
 * themselves. A different item never waits. A conflict (another user saved a
 * different answer to the same item) is never resolved silently: the item
 * stays in conflict until the user picks keepMine or acceptTheirs.
 */
export function useProcedureItemAutosave(
  workOrderId: WorkOrderId,
): ProcedureItemAutosave {
  const cachedExecution = useCachedProcedureExecution(workOrderId);
  const saveItem = useSaveProcedureItemMutation(workOrderId);
  const [statuses, setStatuses] = useState<Record<string, ItemSaveStatus>>({});
  const statusRef = useRef<Record<string, ItemSaveStatus>>({});
  const inFlight = useRef(new Map<string, Promise<void>>());
  const queued = useRef(new Map<string, ItemValue>());

  function setStatus(itemId: string, status: ItemSaveStatus) {
    statusRef.current = { ...statusRef.current, [itemId]: status };
    setStatuses(statusRef.current);
  }

  function storedValue(itemId: string) {
    return (
      cachedExecution()?.responses.find(
        (response) => response.itemId === itemId,
      )?.value ?? null
    );
  }

  async function attempt(
    itemId: string,
    value: ItemValue,
    expectedItemVersion?: number,
  ) {
    setStatus(itemId, { kind: "saving" });
    try {
      await saveItem.mutateAsync({
        itemId,
        value,
        // Read at send time, so a save queued behind another quotes the token
        // the first one returned. Keep mine passes the conflict's token.
        expectedItemVersion:
          expectedItemVersion ??
          procedureItemRevision(cachedExecution(), itemId),
        idempotencyKey: crypto.randomUUID(),
      });
      return true;
    } catch (error) {
      setStatus(
        itemId,
        error instanceof ProcedureItemConflictError
          ? { kind: "conflict", current: error.current }
          : {
              kind: "error",
              message: error instanceof Error ? error.message : "Not saved.",
            },
      );
      return false;
    }
  }

  async function drain(
    itemId: string,
    first: ItemValue,
    firstVersion?: number,
  ) {
    let saved = await attempt(itemId, first, firstVersion);
    while (saved && queued.current.has(itemId)) {
      const next = queued.current.get(itemId) ?? null;
      queued.current.delete(itemId);
      if (valuesEqual(next, storedValue(itemId))) break;
      saved = await attempt(itemId, next);
    }
    queued.current.delete(itemId);
    inFlight.current.delete(itemId);
    if (!saved) throw new Error("The answer was not saved.");
    setStatus(itemId, { kind: "saved" });
  }

  function send(itemId: string, value: ItemValue) {
    const running = inFlight.current.get(itemId);
    if (running) {
      queued.current.set(itemId, value);
      return running;
    }
    if (valuesEqual(value, storedValue(itemId))) {
      // Back to the stored answer: an earlier failure or conflict no longer
      // applies. Only an explicit keepMine reaches this for a conflicted item.
      const kind = statusRef.current[itemId]?.kind;
      if (kind === "error" || kind === "invalid" || kind === "conflict")
        setStatus(itemId, { kind: "idle" });
      return Promise.resolve();
    }
    const started = drain(itemId, value);
    inFlight.current.set(itemId, started);
    return started;
  }

  function sendAnswer(item: ProcedureExecutionItem, answer: ProcedureAnswer) {
    const value = answerToValue(item, answer);
    if (value === undefined) {
      setStatus(item.id, {
        kind: "invalid",
        message: "Enter a valid number.",
      });
      return Promise.reject(new Error("The answer cannot be saved."));
    }
    return send(item.id, value);
  }

  function commit(item: ProcedureExecutionItem, answer: ProcedureAnswer) {
    // A conflict waits for an explicit choice; editing on must not send over
    // the other user's answer.
    if (statusRef.current[item.id]?.kind === "conflict")
      return Promise.reject(new Error("Choose which answer to keep."));
    return sendAnswer(item, answer);
  }

  /**
   * Sends the user's answer over the other user's, quoting the token from the
   * conflict itself. The cached execution may not have been refreshed yet, and
   * it is never patched with the other user's answer.
   */
  function keepMine(item: ProcedureExecutionItem, answer: ProcedureAnswer) {
    const status = statusRef.current[item.id];
    if (status?.kind !== "conflict") return sendAnswer(item, answer);
    const value = answerToValue(item, answer);
    if (value === undefined) {
      // Stay in conflict, with its token, until a valid answer is chosen.
      setStatus(item.id, { ...status, message: "Enter a valid number." });
      return Promise.reject(new Error("The answer cannot be saved."));
    }
    if (valuesEqual(value, status.current.value)) {
      // Both users stored the same answer; there is nothing to send.
      setStatus(item.id, { kind: "idle" });
      return Promise.resolve();
    }
    const started = drain(item.id, value, status.current.version);
    inFlight.current.set(item.id, started);
    return started;
  }

  function acceptTheirs(itemId: string) {
    queued.current.delete(itemId);
    setStatus(itemId, { kind: "idle" });
  }

  async function flushAll() {
    while (inFlight.current.size > 0)
      await Promise.allSettled([...inFlight.current.values()]);
    const blocked = Object.values(statusRef.current).some(
      (status) =>
        status.kind === "conflict" ||
        status.kind === "error" ||
        status.kind === "invalid",
    );
    if (blocked) throw new Error(unsavedMessage);
  }

  return {
    statusFor: (itemId) => statuses[itemId] ?? { kind: "idle" },
    commit,
    keepMine,
    acceptTheirs,
    flushAll,
    hasPending: Object.values(statuses).some(
      (status) => status.kind !== "idle" && status.kind !== "saved",
    ),
  };
}
